import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  CheckCircle2,
  FileSearch,
  MapPin,
  MessageCircleMore,
  Send,
  UserRoundCheck,
} from "lucide-react";
import Container from "../../../components/Container";

function normalizeResponse(json) {
  if (json && Array.isArray(json.content)) {
    return {
      items: json.content,
      totalElements: Number(json.totalElements ?? json.content.length ?? 0),
    };
  }
  if (json && Array.isArray(json.items)) {
    return {
      items: json.items,
      totalElements: Number(json.totalElements ?? json.items.length ?? 0),
    };
  }
  if (Array.isArray(json)) {
    return { items: json, totalElements: json.length };
  }
  return { items: [], totalElements: 0 };
}

function pickLocale(defaultValue, koValue, enValue, locale) {
  if (locale === "ko") return koValue || defaultValue || "";
  return enValue || defaultValue || "";
}

function formatDate(value, locale) {
  if (!value) return "-";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  try {
    return new Intl.DateTimeFormat(locale === "ko" ? "ko-KR" : "en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(dt);
  } catch {
    return dt.toISOString().slice(0, 10);
  }
}

export default function EmploymentMain() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("ko") ? "ko" : "en";

  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const categories = useMemo(() => {
    const data = t("employmentMain.categories.items", { returnObjects: true });
    return Array.isArray(data) ? data : [];
  }, [t]);

  const flowSteps = useMemo(() => {
    const data = t("employmentMain.flow.steps", { returnObjects: true });
    return Array.isArray(data) ? data : [];
  }, [t]);

  const benefits = useMemo(() => {
    const data = t("employmentMain.benefits.items", { returnObjects: true });
    return Array.isArray(data) ? data : [];
  }, [t]);

  const loadLatestJobs = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = new URLSearchParams({
        page: "0",
        size: "6",
        sort: "LATEST",
      });
      const res = await fetch(`/api/employments/list?${params.toString()}`, {
        method: "GET",
        headers: { "Accept-Language": locale },
      });
      if (!res.ok) {
        throw new Error(t("employmentMain.preview.loadError"));
      }

      const json = await res.json();
      if (json?.resultCode && String(json.resultCode) !== "200") {
        throw new Error(
          json?.resultMessage || t("employmentMain.preview.loadError"),
        );
      }

      const payload = json?.data ?? json;
      const normalized = normalizeResponse(payload);
      setJobs(normalized.items);
      setTotal(normalized.totalElements);
    } catch (err) {
      setJobs([]);
      setTotal(0);
      setLoadError(err?.message || t("employmentMain.preview.loadError"));
    } finally {
      setLoading(false);
    }
  }, [locale, t]);

  useEffect(() => {
    loadLatestJobs();
  }, [loadLatestJobs]);

  return (
    <div className="text-slate-900">
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-sky-50 via-white to-white">
        <div className="absolute right-0 top-0 h-72 w-72 translate-x-1/4 -translate-y-1/4 rounded-full bg-blue-100/70 blur-3xl" />
        <Container className="relative py-14 md:py-20">
          <div className="grid items-center gap-8 md:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                <BriefcaseBusiness className="h-3.5 w-3.5" />
                {t("employmentMain.hero.badge")}
              </span>
              <h1 className="mt-4 text-3xl font-extrabold tracking-tight md:text-5xl">
                {t("employmentMain.hero.title")}
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-6 text-slate-600 md:text-base">
                {t("employmentMain.hero.desc")}
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/employment"
                  className="inline-flex items-center rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
                >
                  {t("employmentMain.hero.ctaPrimary")}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="overflow-hidden rounded-3xl border bg-white shadow-sm">
              <img
                src="/images/about/meeting.jpg"
                alt=""
                className="h-56 w-full object-cover md:h-72"
                loading="lazy"
                decoding="async"
              />
              <div className="grid grid-cols-2 gap-3 p-4">
                <div className="rounded-xl bg-slate-50 px-3 py-3 text-center">
                  <div className="text-xs text-slate-500">
                    {t("employmentMain.hero.stats.total")}
                  </div>
                  <div className="mt-1 text-lg font-bold text-slate-900">
                    {total}
                  </div>
                </div>
                <div className="rounded-xl bg-slate-50 px-3 py-3 text-center">
                  <div className="text-xs text-slate-500">
                    {t("employmentMain.hero.stats.process")}
                  </div>
                  <div className="mt-1 text-lg font-bold text-slate-900">
                    4 STEP
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-12 md:py-16">
        <Container>
          <div className="grid gap-3 md:grid-cols-3">
            <Link
              to="/employment"
              className="rounded-2xl border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <FileSearch className="h-5 w-5 text-blue-700" />
              <h3 className="mt-3 text-base font-semibold">
                {t("employmentMain.quickLinks.listTitle")}
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                {t("employmentMain.quickLinks.listDesc")}
              </p>
            </Link>
            <a
              href="#process"
              className="rounded-2xl border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <Send className="h-5 w-5 text-blue-700" />
              <h3 className="mt-3 text-base font-semibold">
                {t("employmentMain.quickLinks.processTitle")}
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                {t("employmentMain.quickLinks.processDesc")}
              </p>
            </a>
            <Link
              to="/contact-us"
              className="rounded-2xl border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <MessageCircleMore className="h-5 w-5 text-blue-700" />
              <h3 className="mt-3 text-base font-semibold">
                {t("employmentMain.quickLinks.inquiryTitle")}
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                {t("employmentMain.quickLinks.inquiryDesc")}
              </p>
            </Link>
          </div>
        </Container>
      </section>

      <section className="border-y bg-slate-50 py-12 md:py-16">
        <Container>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">
                {t("employmentMain.preview.eyebrow")}
              </p>
              <h2 className="mt-2 text-2xl font-bold md:text-3xl">
                {t("employmentMain.preview.title")}
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                {t("employmentMain.preview.desc")}
              </p>
            </div>
            <Link
              to="/employment"
              className="inline-flex items-center rounded-xl border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
            >
              {t("employmentMain.preview.viewAll")}
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>

          <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {loading &&
              Array.from({ length: 6 }).map((_, idx) => (
                <div
                  key={`skeleton-${idx}`}
                  className="animate-pulse rounded-2xl border bg-white p-5"
                >
                  <div className="h-4 w-24 rounded bg-slate-100" />
                  <div className="mt-3 h-5 w-full rounded bg-slate-100" />
                  <div className="mt-2 h-4 w-3/4 rounded bg-slate-100" />
                  <div className="mt-5 h-4 w-1/2 rounded bg-slate-100" />
                </div>
              ))}

            {!loading && loadError && (
              <div className="md:col-span-2 xl:col-span-3 rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
                <p>{loadError}</p>
                <button
                  type="button"
                  onClick={loadLatestJobs}
                  className="mt-3 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700"
                >
                  {t("employmentMain.preview.retry")}
                </button>
              </div>
            )}

            {!loading && !loadError && jobs.length === 0 && (
              <div className="md:col-span-2 xl:col-span-3 rounded-2xl border bg-white p-8 text-center text-sm text-slate-600">
                {t("employmentMain.preview.empty")}
              </div>
            )}

            {!loading &&
              !loadError &&
              jobs.map((job) => {
                const title = pickLocale(
                  job.title,
                  job.titleKo,
                  job.titleEn,
                  locale,
                );
                const company = pickLocale(
                  job.company,
                  job.companyKo,
                  job.companyEn,
                  locale,
                );
                const location = pickLocale(
                  job.locationText,
                  job.locationKo,
                  job.locationEn,
                  locale,
                );
                return (
                  <Link
                    key={job.id}
                    to={`/employment/${job.id}`}
                    className="group rounded-2xl border bg-white p-5 shadow-sm transition hover:shadow-md"
                  >
                    <div className="text-xs text-slate-500">
                      {t("employmentMain.card.posted")}:{" "}
                      {formatDate(job.createdAt, locale)}
                    </div>
                    <h3 className="mt-2 line-clamp-2 text-base font-semibold group-hover:underline">
                      {title || t("employmentMain.card.titleFallback")}
                    </h3>
                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1">
                        <Building2 className="h-3.5 w-3.5" />
                        {company || t("employmentMain.card.companyFallback")}
                      </span>
                      {location && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" />
                          {location}
                        </span>
                      )}
                    </div>
                    <div className="mt-4 flex items-center justify-between border-t pt-3 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays className="h-3.5 w-3.5" />
                        {t("employmentMain.card.deadline")}:{" "}
                        {formatDate(job.deadline, locale)}
                      </span>
                      <span className="font-semibold text-blue-700">
                        {t("employmentMain.card.apply")}
                      </span>
                    </div>
                  </Link>
                );
              })}
          </div>
        </Container>
      </section>

      <section className="py-12 md:py-16">
        <Container>
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl font-bold md:text-3xl">
              {t("employmentMain.categories.title")}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              {t("employmentMain.categories.desc")}
            </p>
          </div>
          <div className="mx-auto mt-7 grid max-w-5xl grid-cols-2 gap-3 md:grid-cols-3">
            {categories.map((item) => (
              <div
                key={item}
                className="rounded-xl border bg-white px-4 py-3 text-center text-sm font-medium text-slate-700 shadow-sm"
              >
                {item}
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section id="process" className="border-y bg-white py-12 md:py-16">
        <Container>
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl font-bold md:text-3xl">
              {t("employmentMain.flow.title")}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              {t("employmentMain.flow.desc")}
            </p>
          </div>
          <ol className="mx-auto mt-8 grid max-w-5xl gap-4 md:grid-cols-4">
            {flowSteps.map((step, idx) => (
              <li
                key={step.title}
                className="rounded-2xl border bg-slate-50 p-5 shadow-sm"
              >
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                  {idx + 1}
                </span>
                <h3 className="mt-3 text-base font-semibold">{step.title}</h3>
                <p className="mt-1 text-sm text-slate-600">{step.desc}</p>
              </li>
            ))}
          </ol>
        </Container>
      </section>

      <section className="py-12 md:py-16">
        <Container>
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl font-bold md:text-3xl">
              {t("employmentMain.benefits.title")}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              {t("employmentMain.benefits.desc")}
            </p>
          </div>
          <div className="mx-auto mt-8 grid max-w-5xl gap-4 md:grid-cols-3">
            {benefits.map((item, idx) => {
              const Icon =
                idx === 0 ? CheckCircle2 : idx === 1 ? UserRoundCheck : Send;
              return (
                <article
                  key={item.title}
                  className="rounded-2xl border bg-white p-6 shadow-sm"
                >
                  <Icon className="h-5 w-5 text-blue-700" />
                  <h3 className="mt-3 text-base font-semibold">{item.title}</h3>
                  <p className="mt-1 text-sm text-slate-600">{item.desc}</p>
                </article>
              );
            })}
          </div>
        </Container>
      </section>

      <section className="pb-16">
        <Container>
          <div className="rounded-3xl border bg-gradient-to-r from-blue-700 to-sky-600 px-6 py-8 text-white shadow-sm md:px-10 md:py-10">
            <h2 className="text-2xl font-bold md:text-3xl">
              {t("employmentMain.cta.title")}
            </h2>
            <p className="mt-2 text-sm text-blue-100 md:text-base">
              {t("employmentMain.cta.desc")}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/employment"
                className="inline-flex items-center rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-blue-700 hover:bg-blue-50"
              >
                {t("employmentMain.hero.ctaPrimary")}
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
